import type { ILoan, ILoanDebts } from "@/modules/loan/common/types";
import { LoanRepaymentStatus, LoanRepaymentType } from "@/modules/transaction/common/enum";
import { formatAmount } from "@/shared/helpers";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { IconInfoFilled } from "@ab.uitools/ui-library/components/SVGIcons";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  penalties?: ILoanDebts;
  loanRepaymentType: LoanRepaymentType;
  contractNumber?: string;
  loan?: ILoan;
}

export const AmountInfo = ({ loan, penalties, loanRepaymentType, contractNumber }: Props) => {
  const { t } = useTranslation();
  const [overdueAmount, setOverdueAmount] = useState<number>(0);

  useEffect(() => {
    setOverdueAmount(0);
  }, [contractNumber]);

  useEffect(() => {
    if (penalties && !overdueAmount) {
      const calculatedAmount = (
        penalties.overdueFee +
        penalties.overdueInterest +
        penalties.overduePrincipal
      ).toFixed(2);
      setOverdueAmount(Number(calculatedAmount));
    }
  }, [penalties]);

  return (
    <div className="mb-32">
      {loanRepaymentType === LoanRepaymentType.FullRepayment && (!!penalties?.prematureRepaymentFee ||
        (loan?.loanRepaymentStatus === LoanRepaymentStatus.Overdue && !!overdueAmount)
      ) && (
        <Divider
          className="my-10"
          color="light"
          type="primary"
          isHorizontal
        />
      )}
      {!!penalties?.prematureRepaymentFee && (
        <div className="mt-10 flexbox align-items-center">
          <div className="flexbox align-items--center">
            <IconInfoFilled size="xsmall" type="tertiary"/>
          </div>
          <div className="flexbox pl-4">
            <Text type="tertiary">
              {t("transfer.loanRepayment.earlyFee")}:
            </Text>
            <Text className="pl-4">
              {
                formatAmount({
                  amount: penalties?.prematureRepaymentFee,
                  currency: loan?.currency,
                })
              }
            </Text>
          </div>
        </div>
      )}
      {(!!penalties?.prematureRepaymentFee &&
        loan?.loanRepaymentStatus === LoanRepaymentStatus.Overdue &&
        !!overdueAmount
      ) && (
        <Divider
          className="mt-10"
          color="light"
          type="primary"
          isHorizontal
        />
      )}
      {loan?.loanRepaymentStatus === LoanRepaymentStatus.Overdue &&
        !!overdueAmount && (
          <div className="mt-10 flexbox align-items-center">
            {!penalties?.prematureRepaymentFee && (
              <div className="flexbox align-items--center">
                <IconInfoFilled size="xsmall" type="tertiary"/>
              </div>
            )}
            <div className="flexbox pl-4">
              <Text type="tertiary">
                {t("transfer.loanRepayment.overdueAmount")}:
              </Text>
              <Text className="pl-4">
                {
                  formatAmount({
                    amount: overdueAmount,
                    currency: loan?.currency,
                  })
                }
              </Text>
            </div>
          </div>
        )
      }
      {(
        (!!penalties?.prematureRepaymentFee ||
          (loan?.loanRepaymentStatus === LoanRepaymentStatus.Overdue && !!overdueAmount)
        ) && (
          (loanRepaymentType === LoanRepaymentType.RegularRepayment && !!loan?.upcomingPaymentAmount) ||
          (loanRepaymentType === LoanRepaymentType.PrincipalRepayment && loan?.remainingPrincipalAmount)
        )) && (
        <Divider
          className="mt-10"
          color="light"
          type="primary"
          isHorizontal
        />
      )}
      {loanRepaymentType === LoanRepaymentType.RegularRepayment &&
        !!loan?.upcomingPaymentAmount && (
          <div className="flexbox pt-10">
            <Text type="tertiary">
              {t("transfer.loanRepayment.nextPayment")}:
            </Text>
            <Text className="pl-4">
              {
                formatAmount({
                  amount: loan?.upcomingPaymentAmount,
                  currency: loan?.currency,
                })}
            </Text>
          </div>
        )
      }
      {loanRepaymentType === LoanRepaymentType.PrincipalRepayment &&
        loan?.remainingPrincipalAmount && (
          <div className="flexbox pt-10">
            <Text type="tertiary">
              {t("transfer.loanRepayment.amountLeft")}:
            </Text>
            <Text className="pl-4">
              {
                formatAmount({
                  amount: loan?.remainingPrincipalAmount,
                  currency: loan?.currency,
                })}
            </Text>
          </div>
        )
      }
    </div>
  );
};
